import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { TUTORIALS } from '../data/tutorials';

const TutorialContext = createContext(null);

const STORAGE_KEY = 'agencyos_tutorials';

function getCompleted(userId) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return all[userId] || {};
  } catch {
    return {};
  }
}

function saveCompleted(userId, key) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (!all[userId]) all[userId] = {};
    all[userId][key] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

export const TutorialProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  const [currentKey, setCurrentKey] = useState(null);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const userId = user?.id || 'anon';

  const startTutorial = useCallback((key) => {
    const tutorial = TUTORIALS[key];
    if (!tutorial) return;
    setCurrentKey(key);
    setSteps(tutorial.steps);
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const close = useCallback(() => {
    setIsActive(false);
  }, []);

  const complete = useCallback((key) => {
    const k = key || currentKey;
    if (k) saveCompleted(userId, k);
    setIsActive(false);
  }, [userId, currentKey]);

  const next = useCallback((stepsLen, sIdx, key, uid) => {
    if (sIdx >= stepsLen - 1) {
      if (key) saveCompleted(uid, key);
      setIsActive(false);
      setStepIndex(0);
    } else {
      setStepIndex(i => i + 1);
    }
  }, []);

  const prev = useCallback(() => {
    setStepIndex(i => Math.max(0, i - 1));
  }, []);

  // Auto-trigger tutorial on route change
  useEffect(() => {
    if (!user) return;
    const uid = user.id;
    const path = location.pathname;
    const completed = getCompleted(uid);

    if (!completed['welcome']) {
      const t = setTimeout(() => startTutorial('welcome'), 900);
      return () => clearTimeout(t);
    }

    if (TUTORIALS[path] && !completed[path]) {
      const t = setTimeout(() => startTutorial(path), 600);
      return () => clearTimeout(t);
    }
  }, [location.pathname, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStep = steps[stepIndex] || null;

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        currentKey,
        stepIndex,
        totalSteps: steps.length,
        startTutorial,
        next: () => next(steps.length, stepIndex, currentKey, userId),
        prev,
        close,
        complete,
        isCompleted: (key) => getCompleted(userId)[key] === true,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => useContext(TutorialContext);
